import React from 'react';
import '../../../../assets/product-selection/itemConfig.scss';
import { useIntersection } from '../../../../hooks';
import { trackEvent } from '../../../../utils/eventTracking';
import { ProductSelectionPresetSearchResult, ProductSelectionResult, PsLangs } from '@brenger/api-client';
import cn from 'classnames';
import { Label } from '../../../../brenger-shared-ui';
import historyBlack from '../../../../assets/images/history-black.png';
import plus from '../../../../assets/images/plus-circle-blue.png';

interface Props {
  listPosition?: number;
  listLength?: number;
  query: string;
  id: string;
  result: (ProductSelectionResult & { label: string }) | (ProductSelectionPresetSearchResult & { label: string });
  bordered: boolean;
  onClick: () => void;
  locale: PsLangs;
}

export const SearchListItem: React.FC<React.PropsWithChildren & Props> = ({
  result,
  bordered,
  onClick,
  locale,
  ...eventData
}) => {
  const intersectionRef = React.useRef(null);
  const isIntersecting = useIntersection(intersectionRef);
  const [eventDispatched, setEventDispatched] = React.useState(false);

  React.useEffect(() => {
    if (isIntersecting && !eventDispatched && eventData.listPosition && eventData.listLength) {
      trackEvent({
        event: 'productSelection',
        action: 'impression',
        listPosition: eventData.listPosition,
        listLength: eventData.listLength,
        query: eventData.query,
        id: eventData.id,
      });

      setEventDispatched(true);
    }
  }, [isIntersecting, eventDispatched]);

  const isPreset = 'branched_from' in result;

  if (isPreset) {
    return (
      <div ref={intersectionRef}>
        <div
          className={cn('ps-modal--section ps-search--result ps-search--result--preset bp flex', {
            bordered,
          })}
          // list position was human numbered, so we need to subtract 1
          data-qa-id={eventData.listPosition ? `ps-search-result-${eventData.listPosition - 1}` : undefined}
          onClick={onClick}
        >
          <div>
            <img style={{ marginTop: '.15em' }} width={24} height={24} src={historyBlack} alt="history" />
          </div>
          <div>
            <div
              // eslint-disable-next-line @typescript-eslint/naming-convention
              dangerouslySetInnerHTML={{ __html: result.label }}
            />
            <div style={{ color: 'rgba(124, 144, 168, 1)' }}>{result.branched_from.name[locale]}</div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <img style={{ marginTop: '.15em' }} width={24} height={24} src={plus} alt="plus" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={intersectionRef}>
      <div
        className={cn('ps-modal--section ps-search--result bp cursor-pointer trigger flex items-center', {
          bordered: bordered,
        })}
        // list position was human numbered, so we need to subtract 1
        data-qa-id={eventData.listPosition ? `ps-search-result-${eventData.listPosition - 1}` : undefined}
        onClick={onClick}
      >
        <div
          // eslint-disable-next-line @typescript-eslint/naming-convention
          dangerouslySetInnerHTML={{ __html: result.label }}
        />

        {result.detected_material?.map(material => {
          return (
            <Label type="black-outline" style={{ margin: '0 0 0 .5em' }} key={material.id}>
              {material.label?.[locale]}
            </Label>
          );
        })}
      </div>
    </div>
  );
};
